// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  Button,
  Icon,
  Image,
  OsModal,
  OsStack,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  DeleteLearnerMixTopicMutation,
  DeleteLearnerMixTopicMutationVariables,
  SaveLearnerMixTopicMutation,
  SaveLearnerMixTopicMutationVariables,
} from "@outschool/gql-frontend-generated";
import { faSparkles, faTrash } from "@outschool/icons";
import { gql, useMutation } from "@outschool/ui-apollo";
import { ErrorMessage } from "@outschool/ui-components-shared";
import React from "react";
import { useAnalytics } from "use-analytics";

import { LEARNER_MIX_TOPICS_QUERY } from "./useTopicsForFilter";

const SAVE_LEARNER_MIX_TOPIC_MUTATION = gql`
  mutation SaveLearnerMixTopic($input: SaveLearnerMixTopicInput!) {
    saveLearnerMixTopic(input: $input) {
      uid
    }
  }
`;

const DELETE_LEARNER_MIX_TOPIC_MUTATION = gql`
  mutation DeleteLearnerMixTopic($uid: ID!) {
    deleteLearnerMixTopic(uid: $uid)
  }
`;

export type ModalMixTopic = {
  uid?: string;
  title: string;
  description: string;
  imageUrl: string;
  sourceTopics?: string[];
  isDefault: boolean;
};

interface MixTopicModalProps {
  topic: ModalMixTopic | null;
  onClose: () => void;
  onSave: () => void;
}

export default function MixTopicModal({
  topic,
  onClose,
  onSave,
}: MixTopicModalProps) {
  const { track } = useAnalytics();
  const [
    deleteLearnerMixTopicMutation,
    { loading: isDeleting, error: deleteError },
  ] = useMutation<
    DeleteLearnerMixTopicMutation,
    DeleteLearnerMixTopicMutationVariables
  >(DELETE_LEARNER_MIX_TOPIC_MUTATION, {
    refetchQueries: [LEARNER_MIX_TOPICS_QUERY],
    awaitRefetchQueries: true,
  });

  const [saveLearnerMixTopicMutation, { loading: isSaving, error: saveError }] =
    useMutation<
      SaveLearnerMixTopicMutation,
      SaveLearnerMixTopicMutationVariables
    >(SAVE_LEARNER_MIX_TOPIC_MUTATION, {
      refetchQueries: [LEARNER_MIX_TOPICS_QUERY],
      awaitRefetchQueries: true,
    });

  let actionPrimary;
  if (topic?.isDefault) {
    actionPrimary = <Button onClick={onClose}>Close</Button>;
  } else if (topic?.uid) {
    actionPrimary = (
      <Button
        onClick={async () => {
          await deleteLearnerMixTopicMutation({
            variables: { uid: topic.uid! },
          });
          track("mixup_machine_delete_topic_click", {
            topicUid: topic.uid,
            topicTitle: topic.title,
          });
          onClose();
        }}
        startIcon={<Icon icon={faTrash} />}
        disabled={isDeleting}
      >
        Remove
      </Button>
    );
  } else if (topic) {
    actionPrimary = (
      <Button
        onClick={async () => {
          await saveLearnerMixTopicMutation({
            variables: {
              input: {
                title: topic.title,
                description: topic.description,
                sourceTopics: topic.sourceTopics as [],
                imageUrl: topic.imageUrl,
              },
            },
          });
          track("mixup_machine_save_topic_click", {
            topicTitle: topic.title,
            sourceTopics: topic.sourceTopics,
          });
          onSave();
          onClose();
        }}
        startIcon={<Icon icon={faSparkles} />}
        disabled={isSaving}
      >
        Save This Mix
      </Button>
    );
  }

  let actionSecondary;
  if (!topic?.isDefault) {
    actionSecondary = (
      <Button onClick={onClose} disabled={isSaving || isDeleting}>
        Close
      </Button>
    );
  }

  return (
    <OsModal
      open={!!topic}
      onClose={onClose}
      title={topic?.title}
      actionPrimary={actionPrimary}
      actionSecondary={actionSecondary}
    >
      <ErrorMessage value={deleteError || saveError} />
      <OsStack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="flex-start"
      >
        <Image
          src={topic?.imageUrl}
          alt={topic?.title}
          sx={(theme: Theme) => ({
            objectFit: "cover",
            borderRadius: "0.5em",
            width: "100%",
            aspectRatio: "1.5",
            [theme.breakpoints.up("sm")]: {
              width: 180,
            },
          })}
        />
        <Typography variant="body1">{topic?.description}</Typography>
      </OsStack>
    </OsModal>
  );
}
